import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { usePayment } from "./context/PaymentContext";
import { useGeo } from "./context/GeoContext";
import { useLanguageDetection } from "./hooks/useLanguageDetection";

import Layout from './components/Layout';
import MainPage from './components/MainPage';
import Privacy from './components/Privacy';
import ResponsibleGame from './components/ResponsibleGame';
import Terms from "./components/Terms";
import KycAmlPolicy from "./components/KycAmlPolicy";
import CookiePolicy from "./components/CookiePolicy";

import Profile from './components/Profile';
import Downbar from "./components/Downbar";
import Sidebar from "./components/Sidebar";
import PrivateRoute from './components/PrivateRoute'; // ✅ Импортируем защиту
import Preloader from "./components/Preloader";
import { useAuth } from "./context/AuthContext";
import AdminRoute from './components/AdminRoute'; // ✅ Импортируем защиту для админов
import Prefs1 from "./components/Prefs1"; // Админ-панель часть 1
import Prefs2 from "./components/Prefs2"; // Админ-панель часть 2
import GamePage from "./components/GamePage";
import GISMainPage from "./components/GISMainPage";
import GISGamePage from "./components/GISGamePage";
import ReferralProgram from './components/ReferralProgram';
import CPAPartner from './components/CPAPartner';
import TelegramLoginPage from './components/TelegramLoginPage';
import TelegramLinkPage from './components/TelegramLinkPage';
import TelegramEntryPage from './components/TelegramEntryPage';
import HowItWorksPage from './components/HowItWorksPage';
import NotFound from './components/NotFound';
import BonusesPage from './components/BonusesPage';
import FreeMoney from './components/FreeMoney';
import EmailVerification from './components/EmailVerification';

// Компонент для вывода логов на экран
const DebugConsole = ({ logs }) => (
  <div style={{
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100vw',
    maxHeight: '30vh',
    overflowY: 'scroll',
    overflowX: 'hidden',
    background: 'rgba(20,20,20,0.95)',
    color: '#00ff00',
    fontSize: '8px',
    fontFamily: 'monospace',
    zIndex: 99999,
    padding: '8px',
    pointerEvents: 'auto', // Включаем прокрутку
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    borderTop: '2px solid #333',
    WebkitOverflowScrolling: 'touch', // Плавная прокрутка на iOS
  }}>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
      borderBottom: '1px solid #333',
      paddingBottom: '4px'
    }}>
      <span style={{ color: '#ffff00', fontSize: '10px', fontWeight: 'bold' }}>
        DEBUG LOG ({logs.length})
      </span>
      <span style={{ color: '#888', fontSize: '8px' }}>
        ↕ scroll
      </span>
    </div>
    <div style={{ maxHeight: 'calc(30vh - 30px)', overflowY: 'auto' }}>
      {logs.map((log, i) => (
        <div key={i} style={{
          marginBottom: '2px',
          lineHeight: '1.2',
          fontSize: '8px'
        }}>
          <span style={{ color: '#666' }}>[{i + 1}]</span> {log}
        </div>
      ))}
    </div>
  </div>
);



const App = () => {

  const { isLoading } = useAuth();
  const [isLoadingApp, setIsLoadingApp] = useState(true);
  const { isBalanceLoaded } = usePayment();
  const { isGeoChecking } = useGeo();

  // Автоматическое определение языка по геолокации
  useLanguageDetection();
  const [debugLogs, setDebugLogs] = useState([]);
  const showDebugLogs = false; // Флаг для управления выводом логов (скрыты для пользователей)

  // Функция для добавления лога
  const addLog = (msg) => {
    // Если msg — объект, преобразуем в строку
    let out;
    if (typeof msg === 'object') {
      try {
        out = JSON.stringify(msg, null, 2);
      } catch {
        out = String(msg);
      }
    } else {
      out = String(msg);
    }
    setDebugLogs(logs => [...logs, out]);
  };

  // Специальная функция тестирования геопроверки
  const testGeoSpecific = async () => {
    addLog('🌍 ТЕСТ ГЕОПРОВЕРКИ...');

    // Сначала проверим простой OPTIONS запрос
    try {
      addLog('🔍 Тест OPTIONS запроса...');
      const optionsResponse = await fetch('/api/client-geo', {
        method: 'OPTIONS'
      });
      addLog(`✅ OPTIONS: ${optionsResponse.status}`);
    } catch (err) {
      addLog(`❌ OPTIONS: ${err.message}`);
    }

    try {
      addLog('📍 Тестируем геолокацию...');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // Увеличили до 5 сек

      const start = Date.now();
      // Используем относительный URL для обхода CORS проблем
      const response = await fetch('/api/client-geo', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
          // Убираем Content-Type для GET запроса
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const duration = Date.now() - start;

      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Геолокация: OK (${duration}ms)`);
        addLog(`📊 IP: ${data.ip || 'N/A'}`);
        addLog(`🌎 Страна: ${data.country || 'N/A'} (${data.countryName || 'N/A'})`);
        addLog(`🔍 Метод: ${data.detectionMethod || 'N/A'}`);
      } else {
        addLog(`❌ Геолокация: ${response.status} ${response.statusText} (${duration}ms)`);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        addLog('⏰ Геолокация: TIMEOUT (>5sec)');
      } else {
        addLog(`❌ Геолокация: ${err.message}`);
      }
    }
  };

  // Экстренная функция восстановления работоспособности
  useEffect(() => {
    window.emergencyFix = () => {
      addLog('🚨 ЭКСТРЕННОЕ ВОССТАНОВЛЕНИЕ');

      // Сбрасываем все блокирующие состояния
      setIsLoadingApp(false);

      // Включаем geo bypass
      localStorage.setItem('geo_bypass', 'true');

      // Перезагружаем страницу
      setTimeout(() => {
        window.location.reload();
      }, 1000);

      addLog('🔄 Перезагрузка через 1 секунду...');
    };

    // Функция для тестирования всех API эндпоинтов
    window.testAllAPIs = async () => {
      addLog('🔍 ТЕСТИРОВАНИЕ ВСЕХ API...');

      const endpoints = [
        '/api/client-geo',
        '/api/gis/games?page=1&per_page=1',
        '/api/gis/games/providers',
        '/api/collections'
      ];

      for (const endpoint of endpoints) {
        try {
          const start = Date.now();
          const response = await fetch(endpoint, {
            credentials: 'include'
          });
          const duration = Date.now() - start;

          addLog(`✅ ${endpoint}: ${response.status} (${duration}ms)`);

          if (!response.ok) {
            addLog(`❌ Error details: ${response.statusText}`);
          }
        } catch (err) {
          addLog(`❌ ${endpoint}: FAILED - ${err.message}`);
        }
      }

      addLog('🏁 API тестирование завершено');
    };

    // Функция для тестирования разных типов запросов
    window.testCORSDebug = async () => {
      addLog('🔍 ТЕСТ CORS...');
      addLog(`🌐 Текущий origin: ${window.location.origin}`);

      // Определяем правильный API URL основываясь на конфигурации
      const apiBase = window.config?.API_URL || 'https://oncaspin.com';

      addLog(`🎯 API URL: ${apiBase}`);

      // Тест 1: Простой запрос без заголовков
      try {
        addLog('1️⃣ Простой GET без заголовков...');
        const response1 = await fetch(`${apiBase}/api/client-geo`);
        addLog(`✅ Простой GET: ${response1.status}`);
      } catch (err) {
        addLog(`❌ Простой GET: ${err.message}`);
      }

      // Тест 2: С credentials
      try {
        addLog('2️⃣ GET с credentials...');
        const response2 = await fetch(`${apiBase}/api/client-geo`, {
          credentials: 'include'
        });
        addLog(`✅ GET credentials: ${response2.status}`);
      } catch (err) {
        addLog(`❌ GET credentials: ${err.message}`);
      }

      // Тест 3: С заголовками
      try {
        addLog('3️⃣ GET с заголовками...');
        const response3 = await fetch(`${apiBase}/api/client-geo`, {
          headers: { 'Accept': 'application/json' }
        });
        addLog(`✅ GET headers: ${response3.status}`);
      } catch (err) {
        addLog(`❌ GET headers: ${err.message}`);
      }

      // Тест 4: Кросс-доменный запрос (если мы на www, попробуем без www и наоборот)
      const crossApiBase = apiBase.includes('www.')
        ? apiBase.replace('www.', '')
        : apiBase.replace('://', '://www.');

      try {
        addLog(`4️⃣ Кросс-домен ${crossApiBase}...`);
        const response4 = await fetch(`${crossApiBase}/api/client-geo`);
        addLog(`✅ Кросс-домен: ${response4.status}`);
      } catch (err) {
        addLog(`❌ Кросс-домен: ${err.message}`);
      }
    };

    window.testGeoSpecific = testGeoSpecific;
  }, []);

  // Диагностические логи для мобильной отладки (только в консоли браузера)
  useEffect(() => {
    console.log('App initialized:', {
      userAgent: navigator.userAgent,
      screen: `${window.screen.width}x${window.screen.height}`,
      connection: navigator.connection ? navigator.connection.effectiveType : 'unknown',
      location: window.location.origin,
      protocol: window.location.protocol,
      host: window.location.host,
      hostname: window.location.hostname
    });
  }, []);

  // Перехватываем все console.log/error/warn/info только если включен дебаг
  useEffect(() => {
    if (!showDebugLogs) return;

    const origLog = console.log;
    const origError = console.error;
    const origWarn = console.warn;
    const origInfo = console.info;
    console.log = (...args) => { origLog(...args); addLog('[log] ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))).join(' ')); };
    console.error = (...args) => { origError(...args); addLog('[error] ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))).join(' ')); };
    console.warn = (...args) => { origWarn(...args); addLog('[warn] ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))).join(' ')); };
    console.info = (...args) => { origInfo(...args); addLog('[info] ' + args.map(a => (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))).join(' ')); };
    return () => {
      console.log = origLog;
      console.error = origError;
      console.warn = origWarn;
      console.info = origInfo;
    };
  }, [showDebugLogs]);

  useEffect(() => {
    setTimeout(() => {
      setIsLoadingApp(false);
    }, 500);
  }, []);



  return (
    <>
      {showDebugLogs && <DebugConsole logs={debugLogs} />}
      <Layout>
        <Routes>
          <Route path="/" element={<GISMainPage />} />
          <Route path="/old-games" element={<MainPage />} />

          <Route element={<PrivateRoute />}>
            <Route path="/profile" element={<Profile />} />
            <Route path="/referral" element={<ReferralProgram />} />
            <Route path="/cpa-partner" element={<CPAPartner />} />
          </Route>

          <Route element={<AdminRoute />}>
            <Route path="/prefs1" element={<Prefs1 />} />
            <Route path="/prefs2" element={<Prefs2 />} />
          </Route>

          <Route path="/game/:gameId" element={<GamePage />} />
          <Route path="/gis-games" element={<GISMainPage />} />
          <Route path="/gis-game/:gameUuid" element={<GISGamePage />} />
          <Route path="/bonuses" element={<BonusesPage />} />
          <Route path="/freemoney" element={<FreeMoney />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/ResponsibleGame" element={<ResponsibleGame />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/KycAmlPolicy" element={<KycAmlPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/telegram-login" element={<TelegramLoginPage />} />
          <Route path="/telegram-link" element={<TelegramLinkPage />} />
          <Route path="/tg" element={<TelegramEntryPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="*" element={<NotFound />} />
        </Routes>

      </Layout>
      <Preloader isLoading={isLoadingApp || !isBalanceLoaded || isGeoChecking} />
    </>


  );
};


export default App;